import { FunctionComponent, useEffect, useRef, useState } from 'react';
import CardsServerPagination from '../cards-server-pagination';
import { Container } from '@/atoms';
import { Filter } from '@/lib/filters';
import { CardBlog } from '@/molecules';
import { Size, Variation } from '@/molecules/card-blog/types';
import { getUrlFromMedia } from '@/utils/utils';
import Link from 'next/link';
import FilterList from '../filter-list/FilterList';
import { DrupalNode, deserialize } from 'next-drupal';
import { pageLimit } from '@/lib/constants';
import { MoonLoader } from 'react-spinners';
import { DrupalJsonApiParams } from 'drupal-jsonapi-params';
import { getParams } from '@/utils/drupal/get-params';

export enum DrupalItemType {
    RECIPE = "node--recipe",
    ARTICLE = "node--article",
}

interface ItemListFilterProps {
    content: DrupalNode[];
    facetCount: number;
    type: DrupalItemType;
    filters?: Filter[];
    queryParams?: DrupalJsonApiParams;
}


export const ItemListFilter: FunctionComponent<ItemListFilterProps> = ({
    content,
    facetCount,
    type,
    filters,
    queryParams = undefined
}) => {

    const SearchType = {
        [DrupalItemType.ARTICLE]: "articles",
        [DrupalItemType.RECIPE]: "recipes",
    };

    const [results, setResults] = useState(content);
    const [totalItems, setTotalItems] = useState(facetCount);
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);

    const firstRender = useRef(true);

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        setResults(content);
        setTotalItems(facetCount);
        setCurrentPage(0);
    }, [content])


    async function handleSubmit(page = 0, activeFilters = undefined) {
        setIsLoading(true);
        const params = queryParams ? new DrupalJsonApiParams(queryParams) : getParams(type, "card");

        if (activeFilters) {
            setSelectedFilters(activeFilters);
        } else {
            activeFilters = selectedFilters;
        }
        for (const filter in activeFilters) {
            const key = filter;
            const val = activeFilters[filter];
            if (Array.isArray(val)) {
                params.addFilter(key, val, "BETWEEN");
            } else {
                params.addFilter(key, val);
            }
        }

        params.addSort("created", "DESC");
        params.addPageLimit(pageLimit);
        params.addPageOffset(page * pageLimit);

        try {
            const response = await fetch(`/api/search/${SearchType[type]}`, {
                method: "POST",
                body: JSON.stringify({
                    deserialize: false,
                    params: params.getQueryObject(),
                }),
            });
            const json = await response.json();
            const results = deserialize(json) as DrupalNode[] || [];
            setResults(results);
            setTotalItems(json.meta.count);
            setCurrentPage(page);
        } catch (error) {
            setTotalItems(0);
            setResults([]);
        };
        setIsLoading(false);
    }
    return (
        <>
            {
                filters ?
                    <Container wrapper zIndex={2} backgroundColor='bg-primary-50' >
                        <FilterList filters={filters} handleSubmit={handleSubmit} selectedFilters={selectedFilters} isLoading={isLoading} />
                    </Container>
                    : null
            }
            <Container wrapper id="scroll-top">
                <CardsServerPagination handleSubmit={handleSubmit} totalItems={totalItems} isLoading={isLoading} currentPage={currentPage}>
                    {
                        results.map((post: any, index: number) => (
                            <CardBlog
                                image={{
                                    src: getUrlFromMedia(post.field_image)
                                }}
                                size={Size.MEDIUM}
                                variation={Variation.DEFAULT}
                                key={index}
                            >
                                {
                                    post.field_difficulty && post.field_servings &&
                                    <CardBlog.Info info={[`${post.field_difficulty}`, `${post.field_servings ? `${post.field_servings} per` : null}`]} />
                                }
                                <CardBlog.Title>
                                    <Link href={post.path.alias}>
                                        <h3>{post.title}</h3>
                                    </Link>
                                </CardBlog.Title>
                                <CardBlog.Link href={post.path.alias}>Ver más</CardBlog.Link>
                            </CardBlog>
                        ))
                    }
                </CardsServerPagination>
            </Container>
        </>
    );
};

ItemListFilter.displayName = 'ItemListFilter';
